import React from 'react';
import { useTranslate } from 'hooks/useTranslate';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { styled, keyframes } from '@mui/system';
import { IS_SNAP } from 'variables/main';
import { getLocalizedPath } from 'helpers/getLocale';
import config from 'config';

// Animation for pop-in with bounce effect
const popInBounce = keyframes`
  0% {
    transform: scale(0.3) translateY(20px);
    opacity: 0;
  }
  50% {
    transform: scale(1.05) translateY(-5px);
    opacity: 0.9;
  }
  70% {
    transform: scale(0.95) translateY(2px);
    opacity: 1;
  }
  100% {
    transform: scale(1) translateY(0);
    opacity: 1;
  }
`;

// Main container positioned at bottom left
const CookieContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isVisible',
})(({ theme, isVisible }) => ({
  position: 'fixed',
  bottom: 20,
  left: 20,
  zIndex: 1000,
  display: 'flex',
  alignItems: 'flex-end',
  gap: 0,
  animation: isVisible ? `${popInBounce} 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55)` : 'none',
  [theme.breakpoints.down('sm')]: {
    bottom: 15,
    left: 15,
    gap: 0
  }
}));

// Text box with rounded corners and green border
const TextBox = styled(Box)(({ theme }) => ({
  backgroundColor: '#FFFEF0', // Light cream/yellow background
  border: '2px solid rgba(121, 201, 18, 0.6)', // Light green border
  borderRadius: 12,
  padding: '16px 20px',
  maxWidth: 300,
  fontFamily: 'monospace',
  fontSize: 12,
  lineHeight: '18px',
  color: '#000000',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  wordBreak: 'break-word',
  [theme.breakpoints.down('sm')]: {
    maxWidth: 240,
    padding: '12px 16px',
    fontSize: 10,
    lineHeight: '16px'
  }
}));

const StyledLink = styled('span')(({ theme }) => ({
  color: theme.palette.primary.main,
  cursor: 'pointer',
  textDecoration: 'underline',
  '&:hover': {
    color: theme.palette.primary.dark
  }
}));

// Hand image container
const HandContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  marginLeft: -12, // Overlap with text box to remove gap
  marginBottom: 15, // Move up
  [theme.breakpoints.down('sm')]: {
    transform: 'scale(0.8)',
    marginLeft: -12,
    marginBottom: -6
  }
}));

// Hand image
const HandImage = styled('img')(() => ({
  width: 80,
  height: 'auto',
  objectFit: 'contain',
  display: 'block'
}));

// Accept label above hand
const AcceptLabel = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main, // Green background
  border: '2px solid rgba(121, 201, 18, 0.8)',
  borderRadius: 8,
  padding: '6px 12px',
  fontFamily: 'monospace',
  fontSize: 12,
  color: '#000000',
  marginBottom: 8,
  transform: 'rotate(15deg)',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': {
    transform: 'rotate(15deg) scale(1.05)',
    borderColor: theme.palette.primary.dark,
    backgroundColor: theme.palette.primary.dark
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: 10,
    padding: '4px 10px'
  }
}));

const CookieConsentCreative = () => {
  const [showBanner, setShowBanner] = React.useState(false);
  const navigate = useNavigate();
  const t = useTranslate('CookieConsent');

  const getCookie = React.useCallback((name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
  }, []);

  const setCookie = React.useCallback((name, value, days) => {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    const expires = `expires=${date.toUTCString()}`;
    document.cookie = `${name}=${value}; ${expires}; path=/`;
  }, []);

  const acceptCookies = React.useCallback(() => {
    setCookie('cookieConsent', 'true', 365);
    // Store in sessionStorage so it doesn't show again in this session
    sessionStorage.setItem('cookieConsent', 'true');
    setShowBanner(false);
  }, [setCookie]);

  const handleNavigate = React.useCallback(() => {
    navigate(getLocalizedPath('/policy/privacy'));
  }, [navigate]);

  React.useEffect(() => {
    // Check both cookie and sessionStorage
    const cookieConsent = getCookie('cookieConsent');
    const sessionConsent = sessionStorage.getItem('cookieConsent');
    
    // Only show if user hasn't accepted (neither cookie nor session)
    if (!cookieConsent && !sessionConsent) {
      // Show banner 5 seconds after page load
      setTimeout(() => {
        setShowBanner(true);
      }, 5000);
    }
  }, [getCookie]);

  if (!showBanner || IS_SNAP) {
    return null;
  }

  return (
    <>
      <CookieContainer isVisible={showBanner}>
        <TextBox>
          <Typography
            component="div"
            sx={{
              fontFamily: 'monospace',
              fontSize: 'inherit',
              lineHeight: 'inherit',
              color: 'inherit'
            }}>
            {t('Description')} <StyledLink onClick={handleNavigate}>
              {t('LearnMore')}
            </StyledLink>
          </Typography>
        </TextBox>
        <HandContainer>
          <AcceptLabel onClick={acceptCookies} role="button" tabIndex={0} onKeyPress={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              acceptCookies();
            }
          }}>
            {t('Accept')}
          </AcceptLabel>
          <HandImage src={`${config.imageSource}/f16078dd-dafe-431a-5a80-52d4fb11bb00/w285h325`} alt="Thumbs up" width="285" height="325" onClick={acceptCookies} style={{ cursor: 'pointer' }} />
        </HandContainer>
      </CookieContainer>
    </>
  );
};

export default CookieConsentCreative;

